/**
 * Width Validation Utilities
 * 
 * Provides validation functions for column width specifications in table schemas.
 * Ensures width values conform to supported formats and business rules.
 * 
 * Constitutional Compliance: Pure validation logic - no business state management.
 */

/**
 * Validates a width specification string
 * @param {string} width - Width specification to validate (e.g., "120px", "25%", "100%")
 * @returns {Object} Validation result with isValid flag, type, and normalized value
 */
export function validateWidth(width) {
  const result = {
    isValid: false,
    type: null,
    normalizedValue: null,
    error: null,
    originalValue: width
  }

  // Handle null/undefined/empty values
  if (!width || typeof width !== 'string') {
    result.error = 'Width must be a non-empty string'
    return result
  }

  const trimmedWidth = width.trim()
  
  // Validate pixel width format
  if (trimmedWidth.endsWith('px')) {
    return validatePixelWidth(trimmedWidth, result)
  }
  
  // Validate percentage width format
  if (trimmedWidth.endsWith('%')) {
    return validatePercentageWidth(trimmedWidth, result)
  }
  
  // Invalid format - no recognized unit
  result.error = `Invalid width format: "${width}". Expected formats: "120px", "25%", or "100%"`
  return result
}

/**
 * Validates pixel-based width specifications
 * @param {string} width - Pixel width string (e.g., "120px")
 * @param {Object} result - Result object to populate
 * @returns {Object} Updated validation result
 */
function validatePixelWidth(width, result) {
  const pixelMatch = width.match(/^(\d+)px$/)
  
  if (!pixelMatch) {
    result.error = `Invalid pixel format: "${width}". Expected positive integer with 'px' (e.g., "120px")`
    return result
  }
  
  const pixels = parseInt(pixelMatch[1], 10)
  
  // Business rule: minimum column width
  if (pixels < 1) {
    result.error = `Pixel width must be at least 1px, got: ${pixels}px`
    return result
  }
  
  // Business rule: maximum reasonable width
  if (pixels > 2000) {
    result.error = `Pixel width ${pixels}px exceeds reasonable maximum (2000px)`
    return result
  }
  
  result.isValid = true
  result.type = 'pixel'
  result.normalizedValue = pixels
  return result
}

/**
 * Validates percentage-based width specifications  
 * @param {string} width - Percentage width string (e.g., "25%")
 * @param {Object} result - Result object to populate
 * @returns {Object} Updated validation result
 */
function validatePercentageWidth(width, result) {
  const percentageMatch = width.match(/^(\d+(?:\.\d+)?)%$/)
  
  if (!percentageMatch) {
    result.error = `Invalid percentage format: "${width}". Expected number with '%' (e.g., "25%")`
    return result
  }
  
  const percentage = parseFloat(percentageMatch[1])
  
  // Business rule: percentage range validation
  if (percentage <= 0) {
    result.error = `Percentage must be greater than 0%, got: ${percentage}%`
    return result
  }
  
  if (percentage > 100) {
    result.error = `Percentage cannot exceed 100%, got: ${percentage}%`
    return result
  }
  
  // Special handling for 100% fill behavior
  if (percentage === 100) {
    result.isValid = true
    result.type = 'fill'
    result.normalizedValue = 100
    return result
  }
  
  result.isValid = true
  result.type = 'percentage'
  result.normalizedValue = percentage
  return result
}

/**
 * Validates width configuration across multiple columns in a table schema
 * @param {Array} columnSchemas - Array of column schema objects
 * @returns {Object} Table-wide validation result
 */
export function validateTableWidthConfiguration(columnSchemas) {
  const result = {
    isValid: true,
    errors: [],
    warnings: [],
    fillColumns: [],
    totalPercentage: 0,
    columnValidations: []
  }
  
  columnSchemas.forEach((schema, index) => {
    const columnResult = {
      columnIndex: index,
      columnKey: schema.key,
      validation: null
    }
    
    if (schema.width) {
      const validation = validateWidth(schema.width)
      columnResult.validation = validation
      
      if (!validation.isValid) {
        result.isValid = false
        result.errors.push(`Column "${schema.key}" (index ${index}): ${validation.error}`)
      } else {
        // Track fill columns for business rule validation
        if (validation.type === 'fill') {
          result.fillColumns.push({
            columnIndex: index,
            columnKey: schema.key
          })
        }
        
        // Track percentage total for overflow detection
        if (validation.type === 'percentage') {
          result.totalPercentage += validation.normalizedValue
        }
      }
    }
    
    result.columnValidations.push(columnResult)
  })
  
  // Business rule: only one fill column allowed
  if (result.fillColumns.length > 1) {
    result.warnings.push(
      `Multiple columns set to "100%" width: ${result.fillColumns.map(col => col.columnKey).join(', ')}. ` +
      'Only the first will fill remaining space, others will auto-size.'
    )
  }
  
  // Business rule: percentage overflow warning
  if (result.totalPercentage > 100) {
    result.warnings.push(
      `Total percentage widths (${result.totalPercentage}%) exceed 100%. ` +
      'Proportional scaling will be applied.'
    )
  }
  
  return result
}

/**
 * Validates width specification format patterns
 * @param {string} width - Width value to check format
 * @returns {Object} Format validation result
 */
export function validateWidthFormat(width) {
  const patterns = {
    pixel: /^[1-9]\d*px$/,
    percentage: /^(0*[1-9]\d*(?:\.\d+)?|100(?:\.0+)?)%$/,
    fill: /^100%$/
  }
  
  if (!width || typeof width !== 'string') {
    return { isValid: false, matchedPattern: null }
  }
  
  const trimmedWidth = width.trim()
  
  for (const [type, pattern] of Object.entries(patterns)) {
    if (pattern.test(trimmedWidth)) {
      return { 
        isValid: true, 
        matchedPattern: type,
        normalizedValue: trimmedWidth
      }
    }
  }
  
  return { 
    isValid: false, 
    matchedPattern: null,
    error: `No valid pattern matched for: "${width}"`
  }
}

/**
 * Provides detailed error context for invalid width specifications
 * @param {string} width - Invalid width specification
 * @returns {Object} Detailed error information and suggestions
 */
export function getWidthValidationHelp(width) {
  const help = {
    originalValue: width,
    issue: null,
    suggestions: [],
    examples: {
      valid: ['120px', '25%', '100%', '50px', '33.5%'],
      invalid: ['120', '25', '120em', 'auto', '-10px', '150%']
    }
  }
  
  if (!width || typeof width !== 'string') {
    help.issue = 'Width value is missing or not a string'
    help.suggestions = ['Provide a width value like "120px" or "25%"']
    return help
  }
  
  const trimmed = width.trim()
  
  // Check for missing units
  if (/^\d+$/.test(trimmed)) {
    help.issue = 'Missing unit specification'
    help.suggestions = [
      `Add unit: "${trimmed}px" for fixed pixels`,
      `Add unit: "${trimmed}%" for percentage width`
    ]
    return help
  }
  
  // Check for unsupported units
  if (/^\d+(em|rem|vw|vh|ch|ex)$/.test(trimmed)) {
    help.issue = 'Unsupported unit type'
    help.suggestions = [
      'Use "px" for fixed pixel widths',
      'Use "%" for responsive percentage widths'
    ]
    return help
  }
  
  // Check for negative values
  if (/^-\d+/.test(trimmed)) {
    help.issue = 'Negative width values not allowed'
    help.suggestions = ['Use positive values only']
    return help
  }
  
  // Check for percentage over 100%
  if (/^([1-9]\d{2,}|1[1-9]\d|10[1-9])%$/.test(trimmed)) {
    help.issue = 'Percentage exceeds 100%'
    help.suggestions = ['Use values between 1% and 100%']
    return help
  }
  
  help.issue = 'Invalid width format'
  help.suggestions = [
    'Use pixel format: "120px"', 
    'Use percentage format: "25%"',
    'Use fill format: "100%" for remaining space'
  ]
  
  return help
}

/**
 * Validates edge case scenarios for width configurations
 * @param {Array} columnSchemas - Array of column schemas
 * @param {Number} containerWidth - Available container width
 * @returns {Object} Edge case validation results
 */
export function validateEdgeCases(columnSchemas, containerWidth) {
  const result = {
    hasEdgeCases: false,
    scenarios: []
  }
  
  // Scenario 1: Very small container width
  if (containerWidth < 200) {
    result.hasEdgeCases = true
    result.scenarios.push({
      type: 'small_container',
      severity: 'warning',
      message: `Small container width (${containerWidth}px) may cause layout issues`,
      recommendation: 'Consider responsive design patterns for mobile'
    })
  }
  
  // Scenario 2: Too many columns with fixed widths
  const fixedWidthColumns = columnSchemas.filter(schema => 
    schema.width && schema.width.endsWith('px')
  )
  
  if (fixedWidthColumns.length > 10) {
    result.hasEdgeCases = true
    result.scenarios.push({
      type: 'too_many_fixed',
      severity: 'warning', 
      message: `${fixedWidthColumns.length} fixed-width columns may reduce flexibility`,
      recommendation: 'Consider using percentage or auto widths for some columns'
    })
  }
  
  // Scenario 3: All columns have explicit widths (no auto-sizing)
  const explicitWidthCount = columnSchemas.filter(schema => schema.width).length
  
  if (explicitWidthCount === columnSchemas.length && explicitWidthCount > 0) {
    result.hasEdgeCases = true
    result.scenarios.push({
      type: 'no_auto_columns',
      severity: 'info',
      message: 'All columns have explicit widths - no auto-sizing available',
      recommendation: 'Ensure width specifications account for all content types'
    })
  }
  
  return result
}